// -- Sidebar --------------------------------------------------
// Toggle collapse, keyboard shortcut (Cmd+B), and mobile dialog,
// plus the named-state API bound per .app-sidebar (AGENTS.md "State API").
// The component's own data-state attribute ("expanded"/"collapsed") is the
// observable state; 'default' means the authored expanded view.

// Shared preamble (AGENTS.md "State API"); the implementation lives in core.js —
// build.ts rewrites this import into a df$.shadcn.shared binding in dist/.
import {
  bindGlobalKeys,
  defussGlobals,
  defussQuery,
  componentState,
  bindComponent,
} from '../../../shared/state-api.js';

const df$ = defussGlobals();
const dfDollar = defussQuery();

const sidebarStates = ['default', 'collapsed'];

// VERIFIED: (verify's API docs gate) the states below are exactly the declared ones, each
// described, and every config field typed, described and named in the code.
/** setState() configs per state - the sidebar's states take none. */
export interface SidebarStateConfigs {
  /** As authored (expanded unless the markup collapses it). */
  default: {};
  /** Icons only (data-state="collapsed"). */
  collapsed: {};
}

/**
 * The markup of a state, for render(): the attributes every state writes -
 * the same as triggerStateChange does on the live element - applied to a
 * detached copy of the authored markup. The e2e render round trip proves
 * the two agree.
 */
function applyMarkup(el, stateName) {
  // 'default' is the authored data-state (expanded when none was authored) -
  // on the authored copy that value is still in place
  dfDollar(el).attr(
    'data-state',
    stateName === 'collapsed' ? 'collapsed' : (el._authoredState ??= dfDollar(el).attr('data-state') || 'expanded'),
  );
}

/**
 * UI side of setState: 'collapsed' docks the rail to icon-width (CSS key is
 * the documented data-state attribute); 'default' restores the authored
 * state (expanded unless the markup says otherwise).
 */
function triggerStateChange(sidebar, stateName, _config) {
  switch (stateName) {
    case 'default':
      sidebar.dataset.state = sidebar._defaultState ?? 'expanded';
      break;
    case 'collapsed':
      sidebar.dataset.state = 'collapsed';
      break;
  }
}

/** Registry-level API; pass the sidebar element explicitly. Unknown names throw. */
export const sidebarApi = componentState({
  component: 'sidebar',
  states: sidebarStates,
  apply: (sidebar, state) => {
    // an explicit state is a decision: the auto-collapse stays out
    sidebar._pinned = true;
    triggerStateChange(sidebar, state.name, state.config);
  },
  read: (sidebar, state) => {
    // reflect reality: trigger clicks and Cmd+B change data-state directly
    return {
      name: sidebar.dataset.state === 'collapsed' ? 'collapsed' : 'default',
      config: state.config,
    };
  },
  markup: (el, state) => applyMarkup(el, state.name),
});

df$.sidebarApi = sidebarApi;
df$.sidebarStates = sidebarStates;

function init() {
  dfDollar('.app-sidebar:not([data-init])')
    .toArray()
    .forEach((sidebar) => {
      sidebar.dataset.init = '';
      // snapshot the authored state + bind per sidebar: `$('#my-sidebar').api.setState('collapsed')`
      sidebar._defaultState = sidebar.dataset.state || 'expanded';
      // a state named before init (a page restoring a remembered choice) pins it;
      // after init data-state-name only MIRRORS the store, so the pin is _pinned
      sidebar._pinned = !!sidebar.dataset.stateName;
      // el.store + el.api (AGENTS.md "State through stores")
      bindComponent(sidebar, sidebarApi);

      // -- Toggle button → collapse/expand -----------------------
      const triggerId = sidebar.id ? `[data-sidebar-trigger="${sidebar.id}"]` : '.sidebar-trigger';
      dfDollar(triggerId)
        .toArray()
        .forEach((trigger) => {
          trigger.addEventListener('click', () => {
            const state = sidebar.dataset.state === 'collapsed' ? 'expanded' : 'collapsed';
            sidebar.dataset.state = state;
            // a user decision: pins against the auto-collapse heuristic
            sidebar._pinned = true;
          });
        });

      // -- Auto-collapse wiring: correct state at first paint + on row resize --
      // observe the row (not the sidebar): the sidebar keeps its authored width
      // (flex-shrink: 0), so only the row's width reports available space.
      document.__sidebarAutoRo?.observe(sidebar.parentElement ?? sidebar);
      autoCollapseSidebar(sidebar);
    });

  // -- Mobile dialog triggers ----------------------------------
  dfDollar('[data-sidebar-mobile]:not([data-init])')
    .toArray()
    .forEach((trigger) => {
      trigger.dataset.init = '';
      const dialog = dfDollar<HTMLDialogElement>('#' + CSS.escape(trigger.dataset.sidebarMobile)).get(0);
      if (!dialog) return;

      trigger.addEventListener('click', () => {
        dialog.showModal();
      });

      // Close button inside the dialog
      dfDollar(dialog)
        .find('.sidebar-mobile-close')
        .toArray()
        .forEach((btn) => {
          btn.addEventListener('click', () => {
            dialog.close();
          });
        });
    });
}

// -- Auto-collapse: too little room → dock to the rail ----------
// A full-width rail only stays useful with content beside it: once the
// sidebar's row (its parent) drops below AUTO_COLLAPSE_BELOW px the
// component docks itself to the icon rail, restoring above
// AUTO_COLLAPSE_ABOVE (hysteresis, so a scrollbar appearing never flickers
// it). An explicit choice always wins: trigger clicks, Cmd+B and
// api.setState (and a data-state-name present before init) set _pinned, and
// while that is set the auto behavior stays out. ponytail: threshold is px-based (authored default is
// 16rem); a custom --sidebar-width beyond ~24rem needs a larger constant.
const AUTO_COLLAPSE_BELOW = 24 * 16; // 384px
const AUTO_COLLAPSE_ABOVE = 28 * 16; // 448px

function autoCollapseSidebar(sidebar) {
  if (sidebar._pinned) return; // deliberate state - never fight it
  // available space = the sidebar's row (a .sidebar-layout or any container);
  // clientWidth of the parent, not the sidebar's own width (flex-shrink: 0
  // keeps the authored width and overflows instead of shrinking).
  const avail = (sidebar.parentElement ?? document.body).clientWidth || window.innerWidth;
  const collapsed = sidebar.dataset.state === 'collapsed';
  if (!collapsed && avail < AUTO_COLLAPSE_BELOW) sidebar.dataset.state = 'collapsed';
  else if (collapsed && avail >= AUTO_COLLAPSE_ABOVE) {
    sidebar.dataset.state = sidebar._defaultState ?? 'expanded';
  }
}

// One shared observer; each init() observes the sidebar's row, so container
// queries / layout resizes re-run the check without a window resize.
if (typeof ResizeObserver !== 'undefined' && !document.__sidebarAutoRo) {
  // the collapse changes layout - so it runs on the next frame, never inside
  // the observer's delivery (that is the "ResizeObserver loop" error)
  const pending = new Set();
  let frame = 0;
  document.__sidebarAutoRo = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target.classList?.contains('app-sidebar')) pending.add(entry.target);
      entry.target.querySelectorAll?.('.app-sidebar').forEach((s) => pending.add(s));
    }
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      pending.forEach(autoCollapseSidebar);
      pending.clear();
    });
  });
}

init();
new MutationObserver(init).observe(document, { childList: true, subtree: true });

// -- Keyboard shortcut: Cmd+B / Ctrl+B ----------------------
// Through the shared global-key listener (src/shared/keys.ts): never fires
// while typing in a field or a contenteditable (where Cmd+B means bold), and
// only claims the key when there is a sidebar to toggle.
if (!document.__sidebarKbInit) {
  document.__sidebarKbInit = true;
  bindGlobalKeys((e) => {
    if (!(e.metaKey || e.ctrlKey) || e.key !== 'b') return;
    // Toggle the first sidebar found on the page
    const sidebar = dfDollar('.app-sidebar').get(0);
    if (!sidebar) return;
    e.preventDefault();
    sidebar.dataset.state = sidebar.dataset.state === 'collapsed' ? 'expanded' : 'collapsed';
    // user decision - pins against the auto-collapse heuristic
    sidebar._pinned = true;
    return true;
  });
}
